import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Activity, Archive, ChevronDown, RefreshCw, Wifi } from "lucide-react";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

const colors = {
  bg: "#030303",
  panel: "#080604",
  header: "#009d8b",
  line: "rgba(255, 212, 93, 0.34)",
  red: "#ff253a",
  green: "#19ff6a",
  gold: "#f7c948",
  muted: "#a1a1aa",
};

function number(value) {
  return Number(value || 0);
}

function fmt(value, digits = 0) {
  return number(value).toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function rowColor(side) {
  if (side === "BUY") return colors.green;
  if (side === "SELL") return colors.red;
  if (side === "WATCH") return colors.gold;
  return "#f8fafc";
}

function pressureLabel(row) {
  if (row.side === "SELL") return "SELL DUMP";
  if (row.side === "BUY") return "BUY LIFT";
  return row.reason || "NORMAL";
}

function displayQty(row, useSavedTotalFallback = false) {
  return useSavedTotalFallback && !number(row.volumeDelta)
    ? row.totalVolume
    : row.volumeDelta;
}

function displayValueCrores(row, useSavedTotalFallback = false) {
  return useSavedTotalFallback && !number(row.pressureValueCrores)
    ? row.tradedValueCrores
    : row.pressureValueCrores;
}

function signed(value, digits = 0) {
  const next = number(value);
  return `${next >= 0 ? "+" : ""}${fmt(next, digits)}`;
}

function timeToMinutes(value) {
  const [hour, minute] = String(value || "00:00").split(":").map(Number);
  return hour * 60 + minute;
}

function minuteLabel(totalMinutes) {
  return `${String(Math.floor(totalMinutes / 60)).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`;
}

function stockSnapshotMinute(snapshot) {
  return String(snapshot?.minute || snapshot?.rows?.find(row => row.time)?.time || "").slice(0, 5);
}

function aggregateStockRows(records, fallbackRows = [], threshold = 3000) {
  if (!records?.length) return fallbackRows || [];

  const bySymbol = new Map();

  records.forEach(record => {
    (record.rows || []).forEach(row => {
      const current = bySymbol.get(row.symbol) || {
        ...row,
        volumeDelta: 0,
        pressureValueCrores: 0,
        firstLtp: row.ltp,
      };
      bySymbol.set(row.symbol, {
        ...current,
        ...row,
        volumeDelta: number(current.volumeDelta) + number(row.volumeDelta),
        pressureValueCrores: Number((number(current.pressureValueCrores) + number(row.pressureValueCrores)).toFixed(2)),
        firstLtp: current.firstLtp,
      });
    });
  });

  const rows = [...bySymbol.values()]
    .map(row => {
      const priceDelta = Number((number(row.ltp) - number(row.firstLtp)).toFixed(2));
      const side = row.volumeDelta >= threshold && priceDelta < 0
        ? "SELL"
        : row.volumeDelta >= threshold && priceDelta > 0
          ? "BUY"
          : "NEUTRAL";

      return {
        ...row,
        priceDelta,
        side,
        alert: row.volumeDelta >= threshold,
        reason: side === "SELL"
          ? "Sell quantity burst"
          : side === "BUY"
            ? "Buy quantity burst"
            : row.reason || "Normal flow",
      };
    })
    .sort((a, b) => number(a.rank) - number(b.rank));

  return rows.length ? rows : fallbackRows || [];
}

function buildStockIntervalRows(snapshots, intervalMinutes, threshold) {
  const start = timeToMinutes("09:15");
  const end = timeToMinutes("15:30");
  const byMinute = new Map();

  (snapshots || []).forEach(snapshot => {
    const minute = stockSnapshotMinute(snapshot);
    if (minute) byMinute.set(minute, snapshot);
  });

  return Array.from({ length: Math.ceil((end - start) / intervalMinutes) }, (_, position) => {
    const intervalStart = start + position * intervalMinutes;
    const intervalEnd = Math.min(intervalStart + intervalMinutes, end);
    const records = [];

    for (let minute = intervalStart + 1; minute <= intervalEnd; minute += 1) {
      const record = byMinute.get(minuteLabel(minute));
      if (record) records.push(record);
    }

    const rows = aggregateStockRows(records, [], threshold);
    const sellRows = rows.filter(row => row.side === "SELL");
    const buyRows = rows.filter(row => row.side === "BUY");

    return {
      serial: position + 1,
      label: `${minuteLabel(intervalStart)} - ${minuteLabel(intervalEnd)}`,
      records,
      rows,
      summary: {
        sellAlerts: sellRows.length,
        buyAlerts: buyRows.length,
        totalSellQty: sellRows.reduce((sum, row) => sum + number(row.volumeDelta), 0),
        totalBuyQty: buyRows.reduce((sum, row) => sum + number(row.volumeDelta), 0),
        totalSellValueCrores: Number(sellRows.reduce((sum, row) => sum + number(row.pressureValueCrores), 0).toFixed(2)),
        totalBuyValueCrores: Number(buyRows.reduce((sum, row) => sum + number(row.pressureValueCrores), 0).toFixed(2)),
      },
    };
  });
}

function weightedDumpCrores(row) {
  return number(row.pressureValueCrores) * (number(row.weight) / 100);
}

function flowMetrics(rows) {
  const buyRows = (rows || []).filter(row => row.side === "BUY");
  const sellRows = (rows || []).filter(row => row.side === "SELL");
  const rawBuyFlowCrores = Number(buyRows.reduce((sum, row) => sum + number(row.pressureValueCrores), 0).toFixed(2));
  const rawSellFlowCrores = Number(sellRows.reduce((sum, row) => sum + number(row.pressureValueCrores), 0).toFixed(2));
  const buyDumpCrores = Number(buyRows.reduce((sum, row) => sum + weightedDumpCrores(row), 0).toFixed(2));
  const sellDumpCrores = Number(sellRows.reduce((sum, row) => sum + weightedDumpCrores(row), 0).toFixed(2));

  return {
    rawBuyFlowCrores,
    rawSellFlowCrores,
    netFlowCrores: Number((rawBuyFlowCrores - rawSellFlowCrores).toFixed(2)),
    buyDumpCrores,
    sellDumpCrores,
    netIndexPressure: Number((buyDumpCrores - sellDumpCrores).toFixed(2)),
  };
}

function estimateNiftyPoints(rows, niftySpot) {
  const weightedReturn = (rows || []).reduce((sum, row) => {
    const currentPrice = number(row.ltp);
    const openingPrice = number(row.firstLtp) || currentPrice - number(row.priceDelta);
    if (!currentPrice || !openingPrice) return sum;
    return sum + (number(row.weight) / 100) * ((currentPrice / openingPrice) - 1);
  }, 0);

  return {
    basketMovePercent: Number((weightedReturn * 100).toFixed(3)),
    estimatedNiftyPoints: niftySpot ? Number((niftySpot * weightedReturn).toFixed(2)) : null,
    niftySpot: number(niftySpot),
  };
}

function buildInstitutionalSignalSeries(snapshots) {
  let heldSide = "NEUTRAL";
  let bullHurdleCrores = 0;
  let bearHurdleCrores = 0;

  return (snapshots || [])
    .filter(snapshot => snapshot?.rows?.length)
    .sort((a, b) => String(stockSnapshotMinute(a)).localeCompare(String(stockSnapshotMinute(b))))
    .map(snapshot => {
      if (snapshot.institutionalSignal) {
        const signal = snapshot.institutionalSignal;
        heldSide = signal.finalSide || signal.rawSide || heldSide;
        bullHurdleCrores = Math.max(bullHurdleCrores, number(signal.bullHurdleCrores), heldSide === "BULL" ? number(signal.buyDumpCrores) : 0);
        bearHurdleCrores = Math.max(bearHurdleCrores, number(signal.bearHurdleCrores), heldSide === "BEAR" ? number(signal.sellDumpCrores) : 0);
        return { ...signal, ...flowMetrics(snapshot.rows) };
      }

      const buyDumpCrores = Number((snapshot.rows || [])
        .filter(row => row.side === "BUY")
        .reduce((sum, row) => sum + weightedDumpCrores(row), 0)
        .toFixed(2));
      const sellDumpCrores = Number((snapshot.rows || [])
        .filter(row => row.side === "SELL")
        .reduce((sum, row) => sum + weightedDumpCrores(row), 0)
        .toFixed(2));
      const rawSide = buyDumpCrores > sellDumpCrores ? "BULL" : sellDumpCrores > buyDumpCrores ? "BEAR" : "NEUTRAL";
      let finalSide = heldSide;
      let holdReason = "";

      if (rawSide === "BEAR") {
        if (heldSide === "BULL" && sellDumpCrores <= bullHurdleCrores) {
          finalSide = "BULL";
          holdReason = "Sell dump has not defeated previous bull dump";
        } else {
          finalSide = "BEAR";
          bearHurdleCrores = Math.max(bearHurdleCrores, sellDumpCrores);
        }
      } else if (rawSide === "BULL") {
        if (heldSide === "BEAR" && buyDumpCrores <= bearHurdleCrores) {
          finalSide = "BEAR";
          holdReason = "Buy dump has not defeated previous bear dump";
        } else {
          finalSide = "BULL";
          bullHurdleCrores = Math.max(bullHurdleCrores, buyDumpCrores);
        }
      } else {
        finalSide = heldSide;
      }

      if (finalSide === "BEAR") bearHurdleCrores = Math.max(bearHurdleCrores, sellDumpCrores);
      if (finalSide === "BULL") bullHurdleCrores = Math.max(bullHurdleCrores, buyDumpCrores);
      heldSide = finalSide;

      return {
        minute: stockSnapshotMinute(snapshot),
        ...flowMetrics(snapshot.rows),
        buyDumpCrores,
        sellDumpCrores,
        rawSide,
        finalSide,
        label: finalSide === "BULL" ? "BULL STRONG" : finalSide === "BEAR" ? "BEAR STRONG" : "NEUTRAL",
        holdReason,
        bullHurdleCrores: Number(bullHurdleCrores.toFixed(2)),
        bearHurdleCrores: Number(bearHurdleCrores.toFixed(2)),
      };
    });
}

function buildIntervalInstitutionalSignal(interval, niftySpot) {
  const metrics = flowMetrics(interval.rows);
  const indexImpact = estimateNiftyPoints(interval.rows, niftySpot);
  const { buyDumpCrores, sellDumpCrores } = metrics;
  const finalSide = buyDumpCrores > sellDumpCrores
    ? "BULL"
    : sellDumpCrores > buyDumpCrores
      ? "BEAR"
      : "NEUTRAL";

  return {
    minute: interval.label,
    ...metrics,
    ...indexImpact,
    rawSide: finalSide,
    finalSide,
    label: finalSide === "BULL" ? "BULL STRONG" : finalSide === "BEAR" ? "BEAR STRONG" : "NEUTRAL",
  };
}

function hasInstitutionalDump(signal) {
  return number(signal?.buyDumpCrores) > 0 || number(signal?.sellDumpCrores) > 0;
}

function momentumRead(signal) {
  const flow = number(signal?.netFlowCrores);
  const points = number(signal?.estimatedNiftyPoints);
  const flowBias = flow > 0 ? "Bullish" : flow < 0 ? "Bearish" : "Neutral";
  const priceMomentum = points > 0 ? "Bullish" : points < 0 ? "Bearish" : "Flat";
  const interpretation = flowBias === "Bearish" && priceMomentum === "Bullish"
    ? "Bearish supply was absorbed; price still held/rallied."
    : flowBias === "Bullish" && priceMomentum === "Bearish"
      ? "Bullish flow was absorbed; price still fell."
      : flowBias === "Bullish" && priceMomentum === "Bullish"
        ? "Buy flow and price momentum agree."
        : flowBias === "Bearish" && priceMomentum === "Bearish"
          ? "Sell flow and price momentum agree."
          : "No confirmed directional alignment yet.";
  return { flowBias, priceMomentum, interpretation };
}

function eventBackground(event) {
  if (event.stage === "UPCOMING") return "rgba(105, 84, 12, 0.82)";
  if (event.side === "BUY") return "rgba(0, 112, 43, 0.72)";
  if (event.side === "SELL") return "rgba(71, 5, 15, 0.92)";
  return "rgba(20, 16, 16, 0.88)";
}

const calendarTabs = [
  { id: "economic", label: "Economic Calendar" },
  { id: "results", label: "Stock Results Calendar" },
  { id: "holidays", label: "Holiday Calendar" },
];

const flagText = { IN: "🇮🇳", US: "🇺🇸", CN: "🇨🇳" };

function impactColor(impact) {
  if (impact === "High") return { background: "#800909", color: "#fecaca" };
  if (impact === "Medium") return { background: "#7a4309", color: "#fde68a" };
  return { background: "#075c6f", color: "#cffafe" };
}

function eventTypeColor(event) {
  if (event === "Results") return { background: "#0b4f82", color: "#dbeafe" };
  if (event === "Dividend") return { background: "#1d1830", color: "#9d6bff" };
  return { background: "#172554", color: "#bfdbfe" };
}

const styles = {
  page: {
    minHeight: "100vh",
    background: colors.bg,
    color: "#f8fafc",
    padding: 18,
    fontFamily: "Consolas, Monaco, 'Courier New', monospace",
  },
  top: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  title: {
    margin: 0,
    color: colors.gold,
    fontSize: 26,
    letterSpacing: 0,
    textShadow: "0 0 14px rgba(247, 201, 72, 0.45)",
  },
  sub: {
    color: colors.muted,
    fontSize: 12,
    marginTop: 4,
  },
  button: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    border: `1px solid ${colors.line}`,
    background: "#11100b",
    color: colors.gold,
    padding: "9px 12px",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 800,
  },
  select: {
    border: `1px solid ${colors.line}`,
    background: "#11100b",
    color: colors.gold,
    padding: "9px 12px",
    borderRadius: 6,
    fontWeight: 800,
    minWidth: 180,
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 10,
    marginBottom: 14,
  },
  stat: {
    border: `1px solid ${colors.line}`,
    background: colors.panel,
    borderRadius: 6,
    padding: 12,
  },
  statLabel: {
    color: colors.muted,
    fontSize: 11,
    textTransform: "uppercase",
  },
  statValue: {
    marginTop: 5,
    color: colors.gold,
    fontSize: 20,
    fontWeight: 900,
  },
  tableWrap: {
    border: `1px solid ${colors.line}`,
    background: "#050302",
    borderRadius: 6,
    overflow: "auto",
    boxShadow: "0 0 24px rgba(255, 37, 58, 0.08)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 1080,
  },
  th: {
    background: colors.header,
    color: "#03120e",
    fontWeight: 900,
    padding: "8px 10px",
    borderRight: "2px solid rgba(0,0,0,0.35)",
    whiteSpace: "nowrap",
    fontSize: 13,
  },
  td: {
    padding: "7px 10px",
    borderTop: "1px solid rgba(255,255,255,0.14)",
    borderRight: "1px solid rgba(255,255,255,0.08)",
    whiteSpace: "nowrap",
    fontWeight: 900,
    fontSize: 13,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: colors.gold,
    fontSize: 16,
    fontWeight: 900,
    margin: "18px 0 8px",
  },
  error: {
    color: "#fecaca",
    background: "rgba(127, 29, 29, 0.45)",
    border: "1px solid rgba(248, 113, 113, 0.5)",
    padding: 10,
    borderRadius: 6,
    marginBottom: 12,
  },
  calendarShell: {
    border: "1px solid #2d3339",
    background: "#171b1d",
    borderRadius: 6,
    overflow: "hidden",
    boxShadow: "0 0 24px rgba(0, 0, 0, 0.28)",
  },
  calendarTabs: {
    display: "inline-flex",
    gap: 6,
    padding: "8px 10px",
    border: "1px solid #3c444a",
    borderRadius: 6,
    margin: 4,
    background: "#101315",
  },
  calendarTab: {
    border: 0,
    borderRadius: 4,
    padding: "10px 16px",
    fontWeight: 800,
    cursor: "pointer",
    color: "#b8afa9",
    background: "#e9e6e1",
  },
  calendarTabActive: {
    background: "#0068c9",
    color: "#ffffff",
  },
  calendarFilters: {
    display: "flex",
    alignItems: "center",
    gap: 18,
    flexWrap: "wrap",
    padding: "12px 12px",
    borderTop: "1px solid #30363b",
    borderBottom: "1px solid #30363b",
  },
  calendarLabel: {
    color: "#f8fafc",
    fontWeight: 900,
    fontSize: 13,
  },
  calendarControl: {
    minWidth: 132,
    border: "1px solid #394148",
    background: "#131719",
    color: "#f8fafc",
    padding: "10px 14px",
    borderRadius: 5,
    fontWeight: 700,
  },
  calendarSearch: {
    minWidth: 260,
    marginLeft: "auto",
    border: "1px solid #394148",
    background: "#101315",
    color: "#f8fafc",
    padding: "10px 14px",
    borderRadius: 5,
    fontWeight: 700,
  },
  calendarTable: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 980,
  },
  calendarTh: {
    color: "#f3f4f6",
    background: "#15191b",
    textAlign: "left",
    padding: "10px 12px",
    borderRight: "1px solid #30363b",
    borderBottom: "1px solid #30363b",
    whiteSpace: "nowrap",
    fontSize: 12,
  },
  calendarTd: {
    color: "#f8fafc",
    padding: "13px 12px",
    borderRight: "1px solid #30363b",
    borderTop: "1px solid #30363b",
    whiteSpace: "nowrap",
    fontSize: 12,
  },
  calendarBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 64,
    padding: "5px 10px",
    borderRadius: 4,
    fontWeight: 900,
  },
  calendarLink: {
    color: "#2aa8ff",
    textDecoration: "none",
    fontWeight: 900,
  },
  liveTabs: {
    display: "inline-flex",
    gap: 6,
    flexWrap: "wrap",
  },
  liveTab: {
    border: `1px solid ${colors.line}`,
    background: "#080604",
    color: colors.gold,
    padding: "9px 12px",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 900,
  },
  liveTabActive: {
    background: colors.gold,
    color: "#080604",
  },
  toggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    color: "#f8fafc",
    fontSize: 12,
    fontWeight: 800,
  },
  contentGrid: {
    display: "block",
    paddingRight: 320,
},
  signalPanel: {
    border: `1px solid ${colors.line}`,
    background: "#060403",
    borderRadius: 6,
    padding: 12,

    position: "fixed",
    right: 18,
    top: 85,

    width: 285,
    maxHeight: "calc(100vh - 100px)",
    overflowY: "auto",

    boxShadow: "0 0 24px rgba(25,255,106,0.08)",
    zIndex: 1000,
},
  signalTitle: {
    color: colors.gold,
    fontSize: 13,
    fontWeight: 900,
    textTransform: "uppercase",
    marginBottom: 10,
  },
  signalValue: {
    fontSize: 22,
    fontWeight: 900,
    margin: "4px 0 10px",
  },
  signalRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    borderTop: "1px solid rgba(255,255,255,0.12)",
    padding: "9px 0",
    fontSize: 12,
    fontWeight: 900,
  },
  signalHistoryRow: {
    display: "grid",
    gridTemplateColumns: "42px 1fr",
    gap: 8,
    borderTop: "1px solid rgba(255,255,255,0.09)",
    padding: "8px 0",
    fontSize: 11,
    fontWeight: 900,
  },
};

export default function Stocks() {
  const [snapshot, setSnapshot] = useState(null);
  const [status, setStatus] = useState(null);
  const [controlling, setControlling] = useState(false);
  const controlVersion = useRef(0);
  const [minuteSnapshots, setMinuteSnapshots] = useState([]);
  const [niftySpotRecords, setNiftySpotRecords] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedHistoryDate, setSelectedHistoryDate] = useState("");
  const [liveGridOpen, setLiveGridOpen] = useState(true);
  const [liveGridTab, setLiveGridTab] = useState("live");
  const [expandedStockInterval, setExpandedStockInterval] = useState("");
  const [selectedSignalInterval, setSelectedSignalInterval] = useState("");
  const [marketCalendarOpen, setMarketCalendarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [calendarTab, setCalendarTab] = useState("results");
  const [calendarData, setCalendarData] = useState({
    economic: [],
    results: [],
    holidays: [],
    sourceLinks: {},
    dateRanges: {},
    upcomingHoliday: null,
  });
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState("");
  const [calendarCountry, setCalendarCountry] = useState("ALL");
  const [calendarImpact, setCalendarImpact] = useState("ALL");
  const [calendarSector, setCalendarSector] = useState("ALL");
  const [calendarEventType, setCalendarEventType] = useState("ALL");
  const [calendarSearch, setCalendarSearch] = useState("");
  const [liquidOnly, setLiquidOnly] = useState(false);
  const [showUsHolidays, setShowUsHolidays] = useState(false);

  const goToStockTab = path => {
    window.location.assign(path);
  };

  const loadLive = useCallback(async (refresh = false) => {
    const version = controlVersion.current;
    setLoading(true);
    setError("");

    try {
      const statusResponse = await fetch(`${API_BASE}/api/stocksserver/status`, { cache: "no-store" });
      const collector = await statusResponse.json();
      if (version !== controlVersion.current) return;
      if (statusResponse.ok && collector.success) setStatus(collector);
      const res = await fetch(`${API_BASE}/api/stocksserver/live${refresh ? "?refresh=1" : ""}`);
      const data = await res.json().catch(() => ({}));
      if (version !== controlVersion.current) return;

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Stocks live fetch failed");
      }

      setSnapshot(data.snapshot || null);
      setMinuteSnapshots(data.minuteSnapshots || []);
      setStatus(data.status || null);
    } catch (err) {
      if (version === controlVersion.current) setError(err.message || "Stocks live fetch failed");
    } finally {
      setLoading(false);
    }
  }, []);

  const setCollectionEnabled = async enabled => {
    controlVersion.current += 1;
    setControlling(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/stocksserver/control`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Could not update collection");
      setStatus(current => ({ ...current, collectionEnabled: data.collectionEnabled }));
      if (enabled) loadLive(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setControlling(false);
    }
  };

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/stocksserver/history`);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        const records = data.records || [];
        setHistory(records);
      }
    } catch {
      setHistory([]);
      setSelectedHistoryDate("");
    }
  }, []);

  const loadNiftySpotHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/market-logger/view?index=NIFTY`);
      const data = await res.json().catch(() => ({}));
      setNiftySpotRecords(res.ok && data.success ? data.records || [] : []);
    } catch {
      setNiftySpotRecords([]);
    }
  }, []);

  const loadCalendar = useCallback(async () => {
    setCalendarLoading(true);
    setCalendarError("");

    try {
      const res = await fetch(`${API_BASE}/api/stocksserver/calendar`);
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Calendar fetch failed");
      }

      setCalendarData({
        economic: data.economic || [],
        results: data.results || [],
        holidays: data.holidays || [],
        sourceLinks: data.sourceLinks || {},
        dateRanges: data.dateRanges || {},
        upcomingHoliday: data.upcomingHoliday || null,
      });
    } catch (err) {
      setCalendarError(err.message || "Calendar fetch failed");
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLive(false);
    loadHistory();
    loadNiftySpotHistory();
    loadCalendar();

    const liveTimer = setInterval(() => loadLive(false), 60000);
    const historyTimer = setInterval(loadHistory, 60000);
    const niftySpotTimer = setInterval(loadNiftySpotHistory, 60000);
    const calendarTimer = setInterval(loadCalendar, 5 * 60000);

    return () => {
      clearInterval(liveTimer);
      clearInterval(historyTimer);
      clearInterval(niftySpotTimer);
      clearInterval(calendarTimer);
    };
  }, [loadCalendar, loadHistory, loadLive, loadNiftySpotHistory]);

  const liveDate = snapshot?.date || "";
  const dateOptions = useMemo(
    () => [...new Set([liveDate, ...history.map(record => record.date)].filter(Boolean))],
    [history, liveDate]
  );
  const selectedHistory = useMemo(() => {
    return history.find(record => record.date === selectedHistoryDate) || null;
  }, [history, selectedHistoryDate]);
  const selectedLiveDate = !selectedHistoryDate || selectedHistoryDate === liveDate;
  const activeStockRecord = selectedLiveDate ? snapshot : selectedHistory;
  const activeMinuteSnapshots = useMemo(
    () => selectedLiveDate ? minuteSnapshots : selectedHistory?.minuteSnapshots || [],
    [minuteSnapshots, selectedHistory?.minuteSnapshots, selectedLiveDate]
  );
  const signalSourceSnapshots = useMemo(
    () => activeMinuteSnapshots.length
      ? activeMinuteSnapshots
      : activeStockRecord?.rows?.length
        ? [activeStockRecord]
        : [],
    [activeMinuteSnapshots, activeStockRecord]
  );
  const niftySpotFor = useCallback((minute, date = activeStockRecord?.date) => {
    const target = timeToMinutes(minute);
    const sameDate = niftySpotRecords
      .filter(record => record.date === date && number(record.spot))
      .sort((a, b) => Math.abs(timeToMinutes(a.time) - target) - Math.abs(timeToMinutes(b.time) - target));
    return number(sameDate[0]?.spot);
  }, [activeStockRecord?.date, niftySpotRecords]);
  const institutionalSignals = useMemo(
    () => {
      const signals = buildInstitutionalSignalSeries(signalSourceSnapshots);
      const snapshots = [...signalSourceSnapshots]
        .filter(snapshot => snapshot?.rows?.length)
        .sort((a, b) => String(stockSnapshotMinute(a)).localeCompare(String(stockSnapshotMinute(b))));
      return signals.map((signal, index) => ({
        ...signal,
        ...estimateNiftyPoints(snapshots[index]?.rows, niftySpotFor(signal.minute, snapshots[index]?.date)),
      }));
    },
    [niftySpotFor, signalSourceSnapshots]
  );
  const rowThreshold = number(status?.thresholdQuantity || activeStockRecord?.thresholdQuantity || 3000);
  const rows = selectedLiveDate
    ? activeStockRecord?.rows || []
    : aggregateStockRows(activeMinuteSnapshots, activeStockRecord?.rows || [], rowThreshold);
  const useSavedTotalFallback = liveGridTab === "live" && (!selectedLiveDate || activeStockRecord?.marketStatus !== "OPEN");
  const summary = activeStockRecord?.summary || {};
  const stockIntervalRows = useMemo(
    () => buildStockIntervalRows(activeMinuteSnapshots, liveGridTab === "5" ? 5 : 1, rowThreshold),
    [activeMinuteSnapshots, liveGridTab, rowThreshold]
  );
  const latestStockInterval = useMemo(
    () => [...stockIntervalRows].reverse().find(row => row.rows.length),
    [stockIntervalRows]
  );
  const intervalInstitutionalSignals = useMemo(
    () => stockIntervalRows
      .filter(row => row.rows.length)
      .map(row => buildIntervalInstitutionalSignal(row, niftySpotFor(row.label.slice(0, 5)))),
    [niftySpotFor, stockIntervalRows]
  );
  const activeInstitutionalSignals = liveGridTab === "live"
    ? institutionalSignals
    : intervalInstitutionalSignals;
  const latestActiveInstitutionalSignal = [...activeInstitutionalSignals]
    .reverse()
    .find(hasInstitutionalDump) || activeInstitutionalSignals[activeInstitutionalSignals.length - 1] || null;
  const displayedInstitutionalSignal = liveGridTab === "live"
    ? latestActiveInstitutionalSignal
    : activeInstitutionalSignals.find(signal => signal.minute === selectedSignalInterval) || latestActiveInstitutionalSignal;
  const displayedMomentumRead = momentumRead(displayedInstitutionalSignal);

  useEffect(() => {
    setExpandedStockInterval("");
    setSelectedSignalInterval("");
  }, [liveGridTab, selectedHistoryDate]);
  const selectedHistoryEvents = useMemo(() => {
    const threshold = number(status?.thresholdQuantity || 3000);
    return (selectedHistory?.dayEvents || [])
      .filter(event =>
        event.stage === "TRIGGERED" &&
        ["BUY", "SELL"].includes(event.side) &&
        number(event.quantity) >= threshold
      )
      .sort((a, b) =>
        number(b.quantity) - number(a.quantity) ||
        number(b.valueCrores) - number(a.valueCrores)
      );
  }, [selectedHistory, status?.thresholdQuantity]);
  const countryOptions = useMemo(() => [...new Set(calendarData.economic.map(row => row.country).filter(Boolean))], [calendarData.economic]);
  const impactOptions = useMemo(() => [...new Set(calendarData.economic.map(row => row.impact).filter(Boolean))], [calendarData.economic]);
  const sectorOptions = useMemo(() => [...new Set(calendarData.results.map(row => row.sector).filter(Boolean))], [calendarData.results]);
  const eventTypeOptions = useMemo(() => [...new Set(calendarData.results.map(row => row.event).filter(Boolean))], [calendarData.results]);
  const economicRows = useMemo(() => {
    return calendarData.economic.filter(row =>
      (calendarCountry === "ALL" || row.country === calendarCountry) &&
      (calendarImpact === "ALL" || row.impact === calendarImpact)
    );
  }, [calendarCountry, calendarData.economic, calendarImpact]);
  const resultRows = useMemo(() => {
    const search = calendarSearch.trim().toLowerCase();
    return calendarData.results.filter(row =>
      (calendarSector === "ALL" || row.sector === calendarSector) &&
      (calendarEventType === "ALL" || row.event === calendarEventType) &&
      (!liquidOnly || row.liquid) &&
      (!search || `${row.stockName} ${row.details} ${row.event}`.toLowerCase().includes(search))
    );
  }, [calendarData.results, calendarEventType, calendarSearch, calendarSector, liquidOnly]);
  const holidayRows = useMemo(() => {
    return calendarData.holidays.filter(row => showUsHolidays || row.country !== "USA");
  }, [calendarData.holidays, showUsHolidays]);

  useEffect(() => {
    if (liveGridTab === "live" || !latestStockInterval) return;
    setExpandedStockInterval(latestStockInterval.label);
  }, [latestStockInterval, liveGridTab]);

  useEffect(() => {
    if (!dateOptions.length) return;
    if (!selectedHistoryDate || !dateOptions.includes(selectedHistoryDate)) {
      setSelectedHistoryDate(dateOptions[0]);
    }
  }, [dateOptions, selectedHistoryDate]);

  return (
    <div style={styles.page}>
      <style>
        {`
          @keyframes stockBlink {
            0%, 100% { filter: brightness(1); box-shadow: inset 0 0 0 rgba(255,255,255,0); }
            50% { filter: brightness(1.7); box-shadow: inset 0 0 22px rgba(255,255,255,0.20); }
          }
          .stock-blink {
            animation: stockBlink 0.85s infinite;
          }
          @media (max-width: 760px) {
            .stocks-title-block h1 { font-size: 20px !important; }
            .stocks-actions { width: 100%; }
            .stocks-actions button,
            .stocks-actions select { flex: 1; justify-content: center; min-width: 0; }
            .stocks-content-grid { grid-template-columns: 1fr !important; }
            .stocks-signal-panel { position: static !important; }
          }
        `}
      </style>

      <div style={styles.top}>
        <div className="stocks-title-block">
          <h1 style={styles.title}>NIFTY 50 STOCKS MARKET REACTION</h1>
          <div style={styles.sub}>
            Direct NSE/BSE desk | {status?.collectionEnabled === false ? "Stopped" : "Auto fetch every minute | Auto EOD save"} | Quantity threshold {fmt(status?.thresholdQuantity || snapshot?.thresholdQuantity || 3000)} | Last {status?.retentionDays || 2} saved days
          </div>
          <div style={{ ...styles.liveTabs, marginTop: 12 }}>
            {[
              { id: "nifty", label: "Nifty 50", path: "/stocks" },
              { id: "banknifty", label: "BankNifty", path: "/bnstocks" },
              { id: "sensex", label: "Sensex", path: "/snstocks" },
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                style={{
                  ...styles.liveTab,
                  ...(tab.id === "nifty" ? styles.liveTabActive : {}),
                }}
                onClick={() => goToStockTab(tab.path)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="stocks-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={styles.button} onClick={() => setCollectionEnabled(true)} disabled={controlling || !status || status.collectionEnabled !== false}>{status?.collectionEnabled === true ? "Started" : "Start"}</button>
          <button style={styles.button} onClick={() => setCollectionEnabled(false)} disabled={controlling || !status || status.collectionEnabled === false}>{status?.collectionEnabled === false ? "Stopped" : "Stop"}</button>
          <button style={styles.button} onClick={() => loadLive(true)} disabled={loading}>
            <RefreshCw size={15} /> {loading ? "Fetching" : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div className="stocks-content-grid" style={styles.contentGrid}>
        <div>
      <div style={styles.statGrid}>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Last Fetch</div>
          <div style={styles.statValue}>{snapshot?.time || "--"}</div>
        </div>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Active Alerts</div>
          <div style={styles.statValue}>{fmt(summary.activeAlerts)}</div>
        </div>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Sell Qty / Value</div>
          <div style={{ ...styles.statValue, color: colors.red }}>
            {fmt(summary.totalSellQty)} / {fmt(summary.totalSellValueCrores, 1)} Cr
          </div>
        </div>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Buy Qty / Value</div>
          <div style={{ ...styles.statValue, color: colors.green }}>
            {fmt(summary.totalBuyQty)} / {fmt(summary.totalBuyValueCrores, 1)} Cr
          </div>
        </div>
      </div>

      <div style={styles.sectionTitle}>
        <button
          type="button"
          style={{
            ...styles.button,
            padding: "9px 12px",
            minWidth: 0,
          }}
          onClick={() => setLiveGridOpen(open => !open)}
        >
          <Wifi size={17} /> Live Nifty 50 Weightage Grid
          <ChevronDown
            size={16}
            style={{
              transform: liveGridOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.16s ease",
            }}
          />
        </button>
        {liveGridOpen && (
          <div style={styles.liveTabs}>
            {[
              { id: "live", label: "Live" },
              { id: "1", label: "1 Min" },
              { id: "5", label: "5 Min" },
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                style={{
                  ...styles.liveTab,
                  ...(liveGridTab === tab.id ? styles.liveTabActive : {}),
                }}
                onClick={() => setLiveGridTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
            <select
              style={{ ...styles.select, minWidth: 190 }}
              value={selectedHistoryDate}
              onChange={event => setSelectedHistoryDate(event.target.value)}
            >
              {dateOptions.map(date => (
                <option key={date} value={date}>
                  {date}
                </option>
              ))}
              {!dateOptions.length && <option value="">No saved dates</option>}
            </select>
          </div>
        )}
      </div>

      {liveGridOpen && liveGridTab === "live" && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                {["Wt", "Time", "Name", "Rate", "Qty", "Value(Cr)", "Side", "Delta", "%", "Reason"].map(head => (
                  <th key={head} style={styles.th}>{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr
                  key={row.symbol}
                  className={row.alert ? "stock-blink" : ""}
                  style={{
                    background: row.side === "BUY"
                      ? "rgba(0, 112, 43, 0.72)"
                      : row.side === "SELL"
                        ? "rgba(71, 5, 15, 0.92)"
                        : "rgba(20, 16, 16, 0.88)",
                    color: rowColor(row.side),
                  }}
                >
                  <td style={styles.td}>{row.rank}. {fmt(row.weight, 2)}%</td>
                  <td style={styles.td}>{row.time}</td>
                  <td style={styles.td}>{row.symbol}</td>
                  <td style={styles.td}>{fmt(row.ltp, 2)}</td>
                  <td style={styles.td}>{fmt(displayQty(row, useSavedTotalFallback))}</td>
                  <td style={styles.td}>{fmt(displayValueCrores(row, useSavedTotalFallback), 1)}</td>
                  <td style={styles.td}>{pressureLabel(row)}</td>
                  <td style={styles.td}>{fmt(row.priceDelta, 2)}</td>
                  <td style={styles.td}>{fmt(row.percentChange, 2)}</td>
                  <td style={styles.td}>{row.reason}</td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td style={styles.td} colSpan={10}>No live Nifty 50 data loaded yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {liveGridOpen && liveGridTab !== "live" && (
        <div style={styles.tableWrap}>
          <table style={{ ...styles.table, minWidth: 1160 }}>
            <thead>
              <tr>
                {["#", "Interval", "Rows", "Sell Alerts", "Buy Alerts", "Sell Qty", "Buy Qty", "Sell Cr", "Buy Cr"].map(head => (
                  <th key={head} style={styles.th}>{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stockIntervalRows.map(row => {
                const isExpanded = expandedStockInterval === row.label;
                return (
                  <React.Fragment key={row.label}>
                    <tr
                      onClick={() => {
                        if (!row.rows.length) return;
                        setSelectedSignalInterval(row.label);
                        setExpandedStockInterval(current => current === row.label ? "" : row.label);
                      }}
                      style={{
                        background: row.rows.length ? "rgba(20, 16, 16, 0.88)" : "rgba(255,255,255,0.015)",
                        color: row.rows.length ? colors.gold : colors.muted,
                        cursor: row.rows.length ? "pointer" : "default",
                      }}
                    >
                      <td style={styles.td}>{row.serial}</td>
                      <td style={styles.td}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {row.rows.length && (
                            <ChevronDown
                              size={14}
                              style={{
                                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                                transition: "transform 0.16s ease",
                              }}
                            />
                          )}
                          {row.label}
                        </span>
                      </td>
                      <td style={styles.td}>{row.rows.length ? row.rows.length : "--"}</td>
                      <td style={{ ...styles.td, color: colors.red }}>{row.rows.length ? fmt(row.summary.sellAlerts) : "--"}</td>
                      <td style={{ ...styles.td, color: colors.green }}>{row.rows.length ? fmt(row.summary.buyAlerts) : "--"}</td>
                      <td style={{ ...styles.td, color: colors.red }}>{row.rows.length ? fmt(row.summary.totalSellQty) : "--"}</td>
                      <td style={{ ...styles.td, color: colors.green }}>{row.rows.length ? fmt(row.summary.totalBuyQty) : "--"}</td>
                      <td style={{ ...styles.td, color: colors.red }}>{row.rows.length ? fmt(row.summary.totalSellValueCrores, 1) : "--"}</td>
                      <td style={{ ...styles.td, color: colors.green }}>{row.rows.length ? fmt(row.summary.totalBuyValueCrores, 1) : "--"}</td>
                    </tr>

                    {isExpanded && row.rows.length > 0 && (
                      <tr>
                        <td colSpan={9} style={{ padding: 0, borderTop: `1px solid ${colors.line}` }}>
                          <div style={{ overflow: "auto" }}>
                            <table style={styles.table}>
                              <thead>
                                <tr>
                                  {["Wt", "Time", "Name", "Rate", "Qty", "Value(Cr)", "Side", "Delta", "%", "Reason"].map(head => (
                                    <th key={head} style={styles.th}>{head}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {row.rows.map(stock => (
                                  <tr
                                    key={`${row.label}-${stock.symbol}`}
                                    className={stock.alert ? "stock-blink" : ""}
                                    style={{
                                      background: stock.side === "BUY"
                                        ? "rgba(0, 112, 43, 0.72)"
                                        : stock.side === "SELL"
                                          ? "rgba(71, 5, 15, 0.92)"
                                          : "rgba(20, 16, 16, 0.88)",
                                      color: rowColor(stock.side),
                                    }}
                                  >
                                    <td style={styles.td}>{stock.rank}. {fmt(stock.weight, 2)}%</td>
                                    <td style={styles.td}>{stock.time}</td>
                                    <td style={styles.td}>{stock.symbol}</td>
                                    <td style={styles.td}>{fmt(stock.ltp, 2)}</td>
                                    <td style={styles.td}>{fmt(stock.volumeDelta)}</td>
                                    <td style={styles.td}>{fmt(stock.pressureValueCrores, 1)}</td>
                                    <td style={styles.td}>{pressureLabel(stock)}</td>
                                    <td style={styles.td}>{signed(stock.priceDelta, 2)}</td>
                                    <td style={styles.td}>{fmt(stock.percentChange, 2)}</td>
                                    <td style={styles.td}>{stock.reason}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={styles.sectionTitle}>
        <button
          type="button"
          style={{
            ...styles.button,
            padding: "9px 12px",
            minWidth: 0,
          }}
          onClick={() => setMarketCalendarOpen(open => !open)}
        >
          <Activity size={17} /> Event Info - Market Calendar
          <ChevronDown
            size={16}
            style={{
              transform: marketCalendarOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.16s ease",
            }}
          />
        </button>
      </div>

      {marketCalendarOpen && (
        <div style={styles.calendarShell}>
          <div style={styles.calendarTabs}>
            {calendarTabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                style={{
                  ...styles.calendarTab,
                  ...(calendarTab === tab.id ? styles.calendarTabActive : {}),
                }}
                onClick={() => setCalendarTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

        {calendarError && <div style={{ ...styles.error, margin: 12 }}>{calendarError}</div>}

        {calendarTab === "economic" && (
          <>
            <div style={styles.calendarFilters}>
              <span style={styles.calendarLabel}>Filters</span>
              <select style={styles.calendarControl} value={calendarCountry} onChange={event => setCalendarCountry(event.target.value)}>
                <option value="ALL">Country</option>
                {countryOptions.map(country => <option key={country} value={country}>{country}</option>)}
              </select>
              <select style={styles.calendarControl} value={calendarImpact} onChange={event => setCalendarImpact(event.target.value)}>
                <option value="ALL">Impact</option>
                {impactOptions.map(impact => <option key={impact} value={impact}>{impact}</option>)}
              </select>
              <span style={styles.calendarControl}>{calendarData.dateRanges.economic || "25 Jun to 14 Jul"}</span>
              {calendarLoading && <span style={{ color: colors.muted, fontSize: 12 }}>Refreshing calendar...</span>}
            </div>

            <div style={{ overflow: "auto" }}>
              <table style={styles.calendarTable}>
                <thead>
                  <tr>
                    {["Date", "Country", "Event", "Impact", "Expected", "Actual", "Previous", "More"].map(head => (
                      <th key={head} style={styles.calendarTh}>{head}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {economicRows.map(row => (
                    <tr key={row.id}>
                      <td style={styles.calendarTd}>{row.displayDate}</td>
                      <td style={styles.calendarTd}>{flagText[row.flag] || row.flag} {row.country}</td>
                      <td style={styles.calendarTd}>{row.event}</td>
                      <td style={styles.calendarTd}><span style={{ ...styles.calendarBadge, ...impactColor(row.impact) }}>{row.impact}</span></td>
                      <td style={{ ...styles.calendarTd, textAlign: "right" }}>{row.expected}</td>
                      <td style={{ ...styles.calendarTd, textAlign: "right" }}>{row.actual}</td>
                      <td style={{ ...styles.calendarTd, textAlign: "right" }}>{row.previous}</td>
                      <td style={styles.calendarTd}><a href={row.link || calendarData.sourceLinks.economic} target="_blank" rel="noreferrer" style={styles.calendarLink}>View Details</a></td>
                    </tr>
                  ))}
                  {!economicRows.length && (
                    <tr><td style={styles.calendarTd} colSpan={8}>No economic calendar rows available.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {calendarTab === "results" && (
          <>
            <div style={styles.calendarFilters}>
              <span style={styles.calendarLabel}>Filters</span>
              <select style={styles.calendarControl} value={calendarSector} onChange={event => setCalendarSector(event.target.value)}>
                <option value="ALL">Sectors</option>
                {sectorOptions.map(sector => <option key={sector} value={sector}>{sector}</option>)}
              </select>
              <select style={styles.calendarControl} value={calendarEventType} onChange={event => setCalendarEventType(event.target.value)}>
                <option value="ALL">Event Type</option>
                {eventTypeOptions.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
              <span style={styles.calendarControl}>{calendarData.dateRanges.results || "25 Jun to 21 Aug"}</span>
              <label style={styles.toggle}>
                <input type="checkbox" checked={liquidOnly} onChange={event => setLiquidOnly(event.target.checked)} />
                Liquid Only
              </label>
              <input
                style={styles.calendarSearch}
                value={calendarSearch}
                onChange={event => setCalendarSearch(event.target.value)}
                placeholder="Search company / underlying"
              />
            </div>

            <div style={{ overflow: "auto" }}>
              <table style={styles.calendarTable}>
                <thead>
                  <tr>
                    {["Date", "Stock Name", "Event", "Price (Chg%)", "Details"].map(head => (
                      <th key={head} style={styles.calendarTh}>{head}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resultRows.map((row, index) => {
                    const previous = resultRows[index - 1];
                    const showDate = !previous || previous.displayDate !== row.displayDate;
                    const sameDateCount = resultRows.filter(item => item.displayDate === row.displayDate).length;

                    return (
                      <tr key={row.id}>
                        {showDate && <td style={styles.calendarTd} rowSpan={sameDateCount}>{row.displayDate}</td>}
                        <td style={styles.calendarTd}>{row.stockName}</td>
                        <td style={styles.calendarTd}><span style={{ ...styles.calendarBadge, ...eventTypeColor(row.event), minWidth: 0 }}>{row.event}</span></td>
                        <td style={{ ...styles.calendarTd, textAlign: "right" }}>{row.price} <span style={{ color: colors.green }}>{row.change}</span></td>
                        <td style={styles.calendarTd}>{row.details}</td>
                      </tr>
                    );
                  })}
                  {!resultRows.length && (
                    <tr><td style={styles.calendarTd} colSpan={5}>No stock result calendar rows available.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {calendarTab === "holidays" && (
          <>
            <div style={{ ...styles.calendarFilters, justifyContent: "center" }}>
              <span style={{ color: "#2a8cff", fontSize: 18 }}>●</span>
              <span>
                Upcoming Holiday: {calendarData.upcomingHoliday?.date || "14 September"}, {calendarData.upcomingHoliday?.weekday || "Monday"} ({calendarData.upcomingHoliday?.days || 80} days) - {calendarData.upcomingHoliday?.name || "Ganesh Chaturthi"}
              </span>
              <label style={{ ...styles.toggle, marginLeft: 36 }}>
                <input type="checkbox" checked={showUsHolidays} onChange={event => setShowUsHolidays(event.target.checked)} />
                Show US Market Holidays
              </label>
            </div>

            <div style={{ overflow: "auto", maxWidth: 760, margin: "0 auto" }}>
              <table style={{ ...styles.calendarTable, minWidth: 640 }}>
                <thead>
                  <tr>
                    {["Sr. No.", "Date", "Weekday", "Holiday"].map(head => (
                      <th key={head} style={styles.calendarTh}>{head}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {holidayRows.map(row => (
                    <tr key={row.id}>
                      <td style={{ ...styles.calendarTd, textAlign: "right" }}>{row.srNo}</td>
                      <td style={styles.calendarTd}>{row.date}</td>
                      <td style={styles.calendarTd}>{row.weekday}</td>
                      <td style={styles.calendarTd}>{flagText[row.flag] || row.flag} {row.holiday}</td>
                    </tr>
                  ))}
                  {!holidayRows.length && (
                    <tr><td style={styles.calendarTd} colSpan={4}>No holiday calendar rows available.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
        </div>
      )}
        </div>

        <aside className="stocks-signal-panel" style={styles.signalPanel}>
          <div style={styles.signalTitle}>Bull / Bear Signal</div>
          <div
            style={{
              ...styles.signalValue,
              color: displayedInstitutionalSignal?.finalSide === "BULL"
                ? colors.green
                : displayedInstitutionalSignal?.finalSide === "BEAR"
                  ? colors.red
                  : colors.gold,
            }}
          >
            {displayedInstitutionalSignal?.label || "WAITING"}
          </div>
          <div style={{ color: colors.muted, fontSize: 11, fontWeight: 800, marginBottom: 8 }}>
            {displayedInstitutionalSignal?.minute
              ? `${liveGridTab === "live" ? "Latest active min" : "Selected interval"} ${displayedInstitutionalSignal.minute}`
              : "No minute dump saved yet"}
          </div>
          <div style={{ ...styles.signalTitle, marginTop: 12, marginBottom: 1 }}>Raw Flow</div>
          <div style={styles.signalRow}>
            <span>Total Buy Flow</span>
            <span style={{ color: colors.green }}>₹{fmt(displayedInstitutionalSignal?.rawBuyFlowCrores, 2)} Cr</span>
          </div>
          <div style={styles.signalRow}>
            <span>Total Sell Flow</span>
            <span style={{ color: colors.red }}>₹{fmt(displayedInstitutionalSignal?.rawSellFlowCrores, 2)} Cr</span>
          </div>
          <div style={styles.signalRow}>
            <span>Net Flow</span>
            <span style={{ color: number(displayedInstitutionalSignal?.netFlowCrores) >= 0 ? colors.green : colors.red }}>
              ₹{signed(displayedInstitutionalSignal?.netFlowCrores, 2)} Cr
            </span>
          </div>
          <div style={{ ...styles.signalTitle, marginTop: 12, marginBottom: 1 }}>Nifty-Weighted Pressure</div>
          <div style={styles.signalRow}>
            <span>Buy Pressure</span>
            <span style={{ color: colors.green }}>{fmt(displayedInstitutionalSignal?.buyDumpCrores, 2)}</span>
          </div>
          <div style={styles.signalRow}>
            <span>Sell Pressure</span>
            <span style={{ color: colors.red }}>{fmt(displayedInstitutionalSignal?.sellDumpCrores, 2)}</span>
          </div>
          <div style={styles.signalRow}>
            <span>Net Index Pressure</span>
            <span style={{ color: number(displayedInstitutionalSignal?.netIndexPressure) >= 0 ? colors.green : colors.red }}>
              {signed(displayedInstitutionalSignal?.netIndexPressure, 2)}
            </span>
          </div>
          <div style={{ ...styles.signalTitle, marginTop: 12, marginBottom: 1 }}>50-Stock Index Estimate</div>
          <div style={styles.signalRow}>
            <span>Estimated Nifty Move</span>
            <span style={{ color: number(displayedInstitutionalSignal?.estimatedNiftyPoints) >= 0 ? colors.green : colors.red }}>
              {displayedInstitutionalSignal?.estimatedNiftyPoints == null ? "--" : `${signed(displayedInstitutionalSignal.estimatedNiftyPoints, 2)} pts`}
            </span>
          </div>
          <div style={styles.signalRow}>
            <span>50-stock basket move</span>
            <span style={{ color: number(displayedInstitutionalSignal?.basketMovePercent) >= 0 ? colors.green : colors.red }}>
              {signed(displayedInstitutionalSignal?.basketMovePercent, 3)}%
            </span>
          </div>
          <div style={{ color: colors.muted, fontSize: 10, fontWeight: 800, lineHeight: 1.4, marginTop: 6 }}>
            Price/weight estimate using {displayedInstitutionalSignal?.niftySpot ? `Nifty ${fmt(displayedInstitutionalSignal.niftySpot, 2)}` : "matching Nifty spot unavailable"}; not a forecast.
          </div>
          <div style={{ ...styles.signalTitle, marginTop: 12, marginBottom: 1 }}>Flow / Momentum Read</div>
          <div style={{ color: displayedMomentumRead.flowBias === "Bullish" ? colors.green : displayedMomentumRead.flowBias === "Bearish" ? colors.red : colors.gold, fontSize: 11, fontWeight: 900, lineHeight: 1.55 }}>
            Flow Bias: {displayedMomentumRead.flowBias}
          </div>
          <div style={{ color: displayedMomentumRead.priceMomentum === "Bullish" ? colors.green : displayedMomentumRead.priceMomentum === "Bearish" ? colors.red : colors.gold, fontSize: 11, fontWeight: 900, lineHeight: 1.55 }}>
            Price Momentum: {displayedMomentumRead.priceMomentum} ({displayedInstitutionalSignal?.estimatedNiftyPoints == null ? "--" : `${signed(displayedInstitutionalSignal.estimatedNiftyPoints, 2)} estimated points`})
          </div>
          <div style={{ color: colors.gold, fontSize: 10, fontWeight: 800, lineHeight: 1.4, marginTop: 4 }}>
            Interpretation: {displayedMomentumRead.interpretation}
          </div>
          {displayedInstitutionalSignal?.holdReason && (
            <div style={{ color: colors.gold, fontSize: 11, fontWeight: 800, lineHeight: 1.4, margin: "8px 0 4px" }}>
              {displayedInstitutionalSignal.holdReason}
            </div>
          )}
        </aside>
      </div>

      <div style={styles.sectionTitle}>
        <Archive size={17} /> Saved EOD History - Last {status?.retentionDays || 2} Days
        <select
          style={styles.select}
          value={selectedHistoryDate}
          onChange={event => setSelectedHistoryDate(event.target.value)}
        >
          {dateOptions.map(date => (
            <option key={date} value={date}>
              {date}
            </option>
          ))}
          {!dateOptions.length && <option value="">No saved dates</option>}
        </select>
      </div>

      <div style={styles.tableWrap}>
        <table style={{ ...styles.table, minWidth: 820 }}>
          <thead>
            <tr>
              {["Date", "Archived", "Sell Qty", "Buy Qty", "Sell Cr", "Buy Cr", "Events"].map(head => (
                <th key={head} style={styles.th}>{head}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {history.map(record => (
              <tr key={record.id} style={{ background: "rgba(20, 16, 16, 0.88)", color: "#f8fafc" }}>
                <td style={styles.td}>{record.date}</td>
                <td style={styles.td}>{record.archivedAt ? new Date(record.archivedAt).toLocaleString("en-IN") : "--"}</td>
                <td style={{ ...styles.td, color: colors.red }}>{fmt(record.summary?.totalSellQty)}</td>
                <td style={{ ...styles.td, color: colors.green }}>{fmt(record.summary?.totalBuyQty)}</td>
                <td style={{ ...styles.td, color: colors.red }}>{fmt(record.summary?.totalSellValueCrores, 1)}</td>
                <td style={{ ...styles.td, color: colors.green }}>{fmt(record.summary?.totalBuyValueCrores, 1)}</td>
                <td style={styles.td}>{fmt(record.dayEvents?.length)}</td>
              </tr>
            ))}
            {!history.length && (
              <tr>
                <td style={styles.td} colSpan={7}>No EOD stock records saved yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.sectionTitle}>
        <Activity size={17} /> Saved Event Info {selectedHistory?.date ? `- ${selectedHistory.date}` : ""}
      </div>

      <div style={styles.tableWrap}>
        <table style={{ ...styles.table, minWidth: 860 }}>
          <thead>
            <tr>
              {["Stage", "Time", "Name", "Current", "Qty", "Value(Cr)", "Reason", "Side"].map(head => (
                <th key={head} style={styles.th}>{head}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {selectedHistoryEvents.map(event => (
              <tr
                key={`${selectedHistory?.date}-${event.id}`}
                style={{
                  background: eventBackground(event),
                  color: rowColor(event.side),
                }}
              >
                <td style={styles.td}>{event.stage || "TRIGGERED"}</td>
                <td style={styles.td}>{event.time}</td>
                <td style={styles.td}>{event.symbol}</td>
                <td style={styles.td}>{fmt(event.ltp, 2)}</td>
                <td style={styles.td}>{fmt(event.quantity)}</td>
                <td style={styles.td}>{fmt(event.valueCrores, 1)}</td>
                <td style={styles.td}>{event.reason}</td>
                <td style={styles.td}>{event.side}</td>
              </tr>
            ))}
            {!selectedHistoryEvents.length && (
              <tr>
                <td style={styles.td} colSpan={8}>No saved events for this date.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
